/**
 * Shared parts of ItemsSlice — plan §5.2. Only **2 of 16** reducers are shared, and that is
 * the honest number: this slice is where the two apps' catalogue models diverge hardest.
 *
 * Both apps mount it at `itemsStore` and both name the slice `"catalogue"` (the same name
 * `catalogueSlice` uses — a pre-existing prefix collision on BOTH platforms, not introduced
 * here; see hazard 1, which resolved the `CartSlice` case the same way it is left here:
 * the two slices' action KEYS are disjoint, so nothing actually collides).
 *
 * What each platform does with the same items payload:
 * - **web** keeps the raw list plus `arrayToMap(payload, "mid")`, and builds channel/menu
 *   projections lazily through 12 web-only reducers (`updateChannelToItemMap`,
 *   `updateMenuChannelMap`, `getOptionMapData`, `setSelectedChannelConfig`, …).
 * - **mobile** does all the parsing eagerly inside `getItemsDataSuccess` via
 *   `getCategoryItemsMap`, and keeps four extra maps (`itemsMidMapping`,
 *   `itemsMidBarcodeMapping`, `itemsNameSkuArr`, `catIdToItemIdMap`, `menuItemsMap`).
 *
 * So `getItemsDataSuccess` is genuinely divergent — same action, different derived state —
 * and only the two fetch-lifecycle reducers below are common.
 *
 * ⚠️ `itemsStore` is in **mobile's** redux-persist whitelist and not web's. Neither platform's
 * state SHAPE changes here, so no persist migration is involved.
 *
 * ⚠️ Neither shared reducer takes an action, so this file needs no `PayloadAction` import.
 * Where a platform file DOES need one it must be `PayloadAction<any>`, never `any` (ledger).
 *
 * ── WEB-ADOPTION ASSESSMENT: DO NOT ADOPT WEB'S DERIVED-CATALOG CACHES ────────────────────
 *
 * The five cache fields in `index.desktop.ts` (`channelToItemMap`, `menuChannelMap`,
 * `optionMapData`, `selectedChannelConfigCache`, `channelItemsMidBarcodeMapping`) were the
 * highest-value item in the adoption plan: pure RTK and plain objects, so they LOOK portable.
 * Examined in the web-adoption pass and deliberately not moved. Four reasons, in order:
 *
 * 1. **They are not a gap.** Four of the five already have a mobile counterpart under a
 *    different name and shape — `channelToItemMap`/`catIdToItemIdMap`,
 *    `menuChannelMap`/`menuItemsMap`, `channelItemsMidBarcodeMapping`/`itemsMidBarcodeMapping`,
 *    and `optionMapData`, which mobile keeps in the native-only `CatalogMapSlice`. Mobile is
 *    not missing catalogue projections; it builds different ones.
 *
 * 2. **A cache only means something inside the model that fills it.** Web's exist because web
 *    derives LAZILY and memoises per channel/menu key through 12 web-only reducers. Mobile
 *    derives EAGERLY in `getItemsDataSuccess`. Copying the fields across without the model
 *    gives mobile state that nothing populates and nothing reads.
 *
 * 3. **The real change is a rewrite, not an adoption.** To get web's caching behaviour mobile
 *    would have to take the lazy model wholesale, delete its eager parse, repoint every
 *    consumer off `catIdToItemIdMap` / `menuItemsMap`, and reconcile or delete
 *    `CatalogMapSlice`. That is mobile's catalogue data layer rewritten.
 *
 * 4. **It is a performance change with no baseline.** Phase 0 of `docs/WEB_ADOPTION_PLAN.md`
 *    requires a device profile before performance work, and none has been taken. Eager parsing
 *    may well be the better choice on a phone — one pass at fetch time instead of repeated
 *    lazy derivation. Adopting web's model on a hunch could make mobile slower.
 *
 * The one genuine gap is small: `selectedChannelConfigCache` has no mobile equivalent, and
 * mobile calls `catalogHelpers.getTheSelectedChannelData(...)` uncached at
 * `components/DeliveryDinein/deliveryModalHelpers.tsx:422`. That is a modal path, not a hot
 * render path, so the cache would buy very little — noted, not actioned.
 *
 * **This needs a design decision backed by measurement — which catalogue strategy both
 * platforms should run — not a convergence pass.**
 */
export interface SharedItemsState {
  isItemsFetching: boolean,
  itemsData: {
    [key: string]: any
  },
  allItemsList: [],
}

export const sharedInitialState: SharedItemsState = {
  isItemsFetching: false,
  itemsData: {},
  allItemsList: [],
};

/**
 * ⚠️ `allItemsList` is declared as the EMPTY TUPLE `[]` by BOTH apps, and both then assign
 * `action.payload` to it. That only compiles because the payload is `any`. The looseness is
 * load-bearing (hazard 10) — widening it to `any[]` here would be a typecheck change the
 * uplift is not making.
 */
export const sharedReducers = {
  /**
   * Shared as of 2026-08-20. The two apps' bodies were identical apart from a stray
   * `console.log('==inside ITEMSLICE getItemsData')` in mobile's copy — debug output with no
   * functional effect. Removed under standing rule 2(a): dead code that is the ONLY thing
   * making the platforms differ. Same call as `NotificationsSlice.clearAllNewOnlineOrders`.
   *
   * ⚠️ Mobile's sibling log in `getItemsDataSuccess` STAYS, because removing it there does not
   * collapse anything — that reducer diverges on real logic.
   */
  getItemsData: (state: SharedItemsState) => {
    state.isItemsFetching = true;
  },
  getItemsDataFail: (state: SharedItemsState) => {
    state.isItemsFetching = false;
  },
};
